import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  portfolioIdAtom,
} from '~/atoms/dashboardAtom';
import { endpointPortfolioCorrelationClusterGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { CorrelationClusterWidget } from './CorrelationClusterWidget';
import type { CorrelationClusterData } from '~/types/visualization';
import { useQuery as useGraphQLQuery } from '@apollo/client';
import {
  GET_PORTFOLIO_NAME,
  GET_PROPOSED_PORTFOLIO_STATS,
} from '../../../../graphql/queries/portfolio';
import { GET_INSTRUMENTS_BY_TICKERS } from '../../../../graphql/queries/instruments';

interface CorrelationAnalysis {
  avg_correlation: number | string;
  max_correlation: number | string;
  min_correlation: number | string;
  asset_correlations: {
    [asset: string]: {
      [otherAsset: string]: number | string;
    };
  };
}

export interface CorrelationMetrics {
  avgCorrelation: number | null;
  maxCorrelation: number | null;
  minCorrelation: number | null;
}

export interface TopCorrelationPair {
  asset1: string;
  asset2: string;
  correlation: number;
  isPositive: boolean;
}

export interface CorrelationClusterWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
  lookbackDays?: number;
  correlationThreshold?: number;
}

export const CorrelationClusterWidgetWrapper: React.FC<
  CorrelationClusterWidgetWrapperProps
> = ({
  widgetId,
  handleRemoveWidget,
  shortDescription,
  lookbackDays = 252,
  correlationThreshold = 0.85,
}) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const snapshotId = latestSnapshotId;

  const {
    data,
    isLoading,
    error,
    refetch: reactQueryRefetch,
  } = useQuery<CorrelationClusterData>({
    queryKey: [
      'correlation-cluster',
      snapshotId,
      lookbackDays,
      correlationThreshold,
    ],
    queryFn: async () => {
      if (!snapshotId) {
        throw new Error('No snapshot ID available');
      }

      const response = await endpointPortfolioCorrelationClusterGET(
        snapshotId,
        {
          lookbackDays,
          correlationThreshold,
          portfolioType: 'proposed', // Default to proposed
        },
      );

      return response;
    },
    enabled: !!snapshotId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Fetch portfolio name using GraphQL
  const { data: portfolioData } = useGraphQLQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  // Fetch portfolio stats for correlation analysis (same as ProposedPortfolioWidget)
  const { data: statsData } = useGraphQLQuery(GET_PROPOSED_PORTFOLIO_STATS, {
    variables: { snapshotId },
    skip: !snapshotId,
  });

  useEffect(() => {
    if (snapshotId) {
      reactQueryRefetch();
    }
  }, [snapshotId, reactQueryRefetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      reactQueryRefetch();
    }
  }, [triggerHardRefetch, reactQueryRefetch]);

  const portfolioName = portfolioData?.EQPortfolio?.[0]?.name;

  // Process correlation data from portfolio stats
  const { correlationMetrics, topCorrelatedPairs, correlationTickers } =
    useMemo(() => {
      const analysis = statsData?.EQPortfoliosAnalysis?.[0];
      const correlationAnalysis = analysis?.analysis_data
        ?.correlation_analysis as CorrelationAnalysis | undefined;

      const metrics: CorrelationMetrics = {
        avgCorrelation: null,
        maxCorrelation: null,
        minCorrelation: null,
      };

      const pairs: TopCorrelationPair[] = [];
      const tickers = new Set<string>();

      if (correlationAnalysis) {
        // Extract correlation metrics
        if (correlationAnalysis.avg_correlation !== undefined) {
          metrics.avgCorrelation = parseFloat(
            String(correlationAnalysis.avg_correlation),
          );
        }
        if (correlationAnalysis.max_correlation !== undefined) {
          metrics.maxCorrelation = parseFloat(
            String(correlationAnalysis.max_correlation),
          );
        }
        if (correlationAnalysis.min_correlation !== undefined) {
          metrics.minCorrelation = parseFloat(
            String(correlationAnalysis.min_correlation),
          );
        }

        // Extract top asset correlations
        if (correlationAnalysis.asset_correlations) {
          const allCorrelations: {
            asset1: string;
            asset2: string;
            correlation: number;
            absCorrelation: number;
          }[] = [];
          const seenPairs = new Set<string>();

          Object.entries(correlationAnalysis.asset_correlations).forEach(
            ([asset1, correlations]) => {
              Object.entries(correlations).forEach(([asset2, corrValue]) => {
                const correlationValue = parseFloat(String(corrValue));
                if (
                  !isNaN(correlationValue) &&
                  Math.abs(correlationValue) > 0
                ) {
                  // Create a sorted pair key to avoid duplicates
                  const pairKey = [asset1, asset2].sort().join(',');

                  if (!seenPairs.has(pairKey)) {
                    seenPairs.add(pairKey);
                    allCorrelations.push({
                      asset1,
                      asset2,
                      correlation: correlationValue,
                      absCorrelation: Math.abs(correlationValue),
                    });
                  }
                }
              });
            },
          );

          // Sort by absolute correlation value and take top 6
          const top6 = allCorrelations
            .sort((a, b) => b.absCorrelation - a.absCorrelation)
            .slice(0, 6);

          top6.forEach((item) => {
            pairs.push({
              asset1: item.asset1,
              asset2: item.asset2,
              correlation: item.correlation,
              isPositive: Math.abs(item.correlation) < 0.7,
            });
            // Add both original ticker and with common suffixes for instrument lookup
            tickers.add(item.asset1);
            tickers.add(item.asset2);
            // Also add with .US suffix as full_ticker may use this format
            if (!item.asset1.includes('.')) {
              tickers.add(`${item.asset1}.US`);
            }
            if (!item.asset2.includes('.')) {
              tickers.add(`${item.asset2}.US`);
            }
          });
        }
      }

      return {
        correlationMetrics: metrics,
        topCorrelatedPairs: pairs,
        correlationTickers: Array.from(tickers),
      };
    }, [statsData]);

  // Fetch company names for correlation tickers
  const { data: instrumentsData } = useGraphQLQuery(
    GET_INSTRUMENTS_BY_TICKERS,
    {
      variables: { tickers: correlationTickers },
      skip: correlationTickers.length === 0,
    },
  );

  // Build ticker to company name map
  const correlationTickerNames = useMemo(() => {
    const map = new Map<string, string>();
    if (instrumentsData?.EQInstrument) {
      instrumentsData.EQInstrument.forEach(
        (instrument: { ticker: string; full_ticker: string; name: string }) => {
          if (instrument.name) {
            // Add all variations of the ticker for flexible lookup
            map.set(instrument.ticker, instrument.name);
            map.set(instrument.full_ticker, instrument.name);
            map.set(instrument.ticker.toUpperCase(), instrument.name);
            map.set(instrument.full_ticker.toUpperCase(), instrument.name);
            // Add base ticker (without suffix)
            const baseTicker = instrument.full_ticker.split('.')[0];
            if (baseTicker) {
              map.set(baseTicker, instrument.name);
              map.set(baseTicker.toUpperCase(), instrument.name);
            }
          }
        },
      );
    }
    return map;
  }, [instrumentsData]);

  // Merge portfolio name into data if available
  const enrichedData = data
    ? {
        ...data,
        portfolio_name: portfolioName || data.portfolio_name,
      }
    : null;

  return (
    <CorrelationClusterWidget
      data={enrichedData}
      isLoading={isLoading}
      error={error as Error | null}
      refetch={reactQueryRefetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription}
      snapshotId={snapshotId}
      lookbackDays={lookbackDays}
      correlationThreshold={correlationThreshold}
      portfolioId={portfolioId}
      portfolioName={portfolioName}
      correlationMetrics={correlationMetrics}
      topCorrelatedPairs={topCorrelatedPairs}
      correlationTickerNames={correlationTickerNames}
    />
  );
};
